import Service, { type Registry as Services, service } from '@ember/service';
import config from 'uplisting-frontend/config/environment';

const REMOTE_NAME = 'uplistingReact';

export interface IReactHostConfig {
  apiHost: string;
  getToken(): string | undefined;
  onError(error: unknown): void;
}

export interface IReactElement extends HTMLElement {
  props: Record<string, unknown>;
  hostConfig?: IReactHostConfig;
}

interface IElementsModule {
  defineElements(): void;
}

export default class ReactRemoteService extends Service {
  @service session!: Services['session'];
  @service sentry!: Services['sentry'];

  #defined?: Promise<void>;

  get hostConfig(): IReactHostConfig {
    return {
      apiHost: config.API_HOST,
      getToken: () => this.token,
      onError: (error: unknown) => this.sentry.captureException(error),
    };
  }

  private get token(): string | undefined {
    return (
      this.session?.data?.authenticated?.idToken ||
      this.session?.data?.authenticated?.access_token
    );
  }

  defineElements(): Promise<void> {
    this.#defined ??= this.load().catch((error: unknown) => {
      this.#defined = undefined;

      throw error;
    });

    return this.#defined;
  }

  private async load(): Promise<void> {
    const { entry } = config.reactRemote;

    if (!entry) {
      throw new Error('config.reactRemote.entry is not set');
    }

    await this.installReactRefreshPreamble(entry);

    const { createInstance } = await import('@module-federation/runtime');

    const instance = createInstance({
      name: 'uplistingFrontend',
      remotes: [{ name: REMOTE_NAME, entry }],
    });

    const module = await instance.loadRemote<IElementsModule>(
      `${REMOTE_NAME}/elements`,
    );

    module?.defineElements();
  }

  // Vite's React Fast Refresh expects two things of the page that runs its
  // modules, both of which the remote's own index.html normally provides and
  // this app does not:
  //
  //   - `globalThis.__MF_REACT_REFRESH_URL__`. The federation plugin's shim
  //     reads it when page origin != remote origin; unset, it guesses
  //     `location.origin + '/@react-refresh'`, which 404s here.
  //   - the refresh preamble. Without it every transformed module throws
  //     "can't detect preamble", taking the whole remote down.
  //
  // Only a Vite dev server serves these URLs, and only a dev build of the
  // remote imports them — but which kind of remote `entry` points at is not
  // knowable from here (a dev host can point at a deployed one). So: never
  // probe from a production host, and treat a failed probe as "already built"
  // rather than an error. Gating on IS_DEV alone would break a dev host
  // pointed at a deployed remote.
  private async installReactRefreshPreamble(entry: string): Promise<void> {
    if (!config.IS_DEV) {
      return;
    }

    const target = globalThis as {
      __MF_REACT_REFRESH_URL__?: string;
      $RefreshReg$?: unknown;
      $RefreshSig$?: unknown;
    };

    if (target.$RefreshReg$) {
      return;
    }

    const runtime = new URL('/@mf-react-refresh-local', entry).href;

    // Set before importing, and import the runtime rather than the shim: the
    // remote's `/@react-refresh` IS the shim, so reading the runtime through it
    // deadlocks — the shim needs this global to resolve, and the global is what
    // the import was for.
    target.__MF_REACT_REFRESH_URL__ = runtime;

    try {
      const { injectIntoGlobalHook } = (await import(
        /* webpackIgnore: true */ runtime
      )) as { injectIntoGlobalHook(window: Window): void };

      injectIntoGlobalHook(window);

      target.$RefreshReg$ = () => undefined;
      target.$RefreshSig$ = () => (type: unknown) => type;
    } catch {
      delete target.__MF_REACT_REFRESH_URL__;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'react-remote': ReactRemoteService;
  }
}
